/**
 * JWT Token Utility Functions
 * Handles token decoding, validation, and contractor_id checking
 */

/**
 * Decodes a JWT token and returns the payload
 * @param {string} token - The JWT token to decode
 * @returns {object|null} - The decoded payload or null if invalid
 */
export const decodeToken = (token) => {
  try {
    if (!token) {
      // console.log("🔍 [DECODE] No token provided");
      return null;
    }

    // Remove 'Bearer ' prefix if present
    const cleanToken = token.replace("Bearer ", "");
    if (cleanToken !== token) {
      // console.log('🔍 [DECODE] Removed "Bearer " prefix');
    }

    // Split the token into parts
    const parts = cleanToken.split(".");

    if (parts.length !== 3) {
      return null;
    }

    // Decode the payload (second part)
    const payload = parts[1];

    const decodedPayload = JSON.parse(atob(payload));

    return decodedPayload;
  } catch (error) {
    console.error("❌ [DECODE] Error decoding JWT token:", error);
    return null;
  }
};

/**
 * Checks if a token contains contractor_id
 * @param {string} token - The JWT token to check
 * @returns {boolean} - True if contractor_id exists, false otherwise
 */
export const hasContractorId = (token) => {
  try {
    const decoded = decodeToken(token);
    if (!decoded) {
      return false;
    }

    // Check if contractor_id exists in the token payload
    const hasContractor =
      decoded.hasOwnProperty("contractor_id") &&
      decoded.contractor_id !== null &&
      decoded.contractor_id !== undefined;

    return hasContractor;
  } catch (error) {
    console.error(
      "❌ [CONTRACTOR_ID] Error checking contractor_id in token:",
      error
    );
    return false;
  }
};

/**
 * Gets the contractor_id from a token
 * @param {string} token - The JWT token to extract from
 * @returns {number|null} - The contractor_id or null if not found
 */
export const getContractorId = (token) => {
  try {
    console.log("🔍 [GET_CONTRACTOR_ID] Extracting contractor_id...");
    const decoded = decodeToken(token);
    if (!decoded) {
      console.log(
        "❌ [GET_CONTRACTOR_ID] Cannot extract contractor_id - token decode failed"
      );
      return null;
    }

    const contractorId = decoded.contractor_id || null;
    console.log(
      "🔍 [GET_CONTRACTOR_ID] Extracted contractor_id:",
      contractorId
    );
    console.log(
      "🔍 [GET_CONTRACTOR_ID] contractor_id type:",
      typeof contractorId
    );

    return contractorId;
  } catch (error) {
    console.error(
      "❌ [GET_CONTRACTOR_ID] Error extracting contractor_id from token:",
      error
    );
    return null;
  }
};

/**
 * Checks if a token is expired
 * @param {string} token - The JWT token to check
 * @returns {boolean} - True if expired, false otherwise
 */
export const isTokenExpired = (token) => {
  try {
    const decoded = decodeToken(token);
    if (!decoded) {
      return true;
    }

    const currentTime = Math.floor(Date.now() / 1000);
    const tokenExp = decoded.exp;

    const expired = tokenExp < currentTime;

    return expired;
  } catch (error) {
    console.error("❌ [EXPIRATION] Error checking token expiration:", error);
    return true;
  }
};

/**
 * Validates a token for basic JWT structure and expiration
 * @param {string} token - The JWT token to validate
 * @returns {object} - Validation result with isValid and error properties
 */
export const validateToken = (token) => {
  try {
    console.log("🔍 [BASIC_VALIDATION] Starting basic token validation...");

    if (!token) {
      console.log("❌ [BASIC_VALIDATION] No token provided");
      return { isValid: false, error: "No token provided" };
    }

    const decoded = decodeToken(token);
    if (!decoded) {
      console.log("❌ [BASIC_VALIDATION] Invalid token format");
      return { isValid: false, error: "Invalid token format" };
    }

    if (isTokenExpired(token)) {
      console.log("❌ [BASIC_VALIDATION] Token expired");
      return { isValid: false, error: "Token expired" };
    }

    console.log("✅ [BASIC_VALIDATION] Basic validation passed");
    return { isValid: true, error: null };
  } catch (error) {
    console.error(
      "❌ [BASIC_VALIDATION] Error during basic validation:",
      error
    );
    return { isValid: false, error: error.message };
  }
};

/**
 * Comprehensive token validation that checks for contractor_id
 * This is the main function to use for security validation
 * @param {string} token - The JWT token to validate
 * @returns {object} - Validation result with isValid, hasContractorId, and error properties
 */
export const validateTokenForSecurity = (token) => {
  try {
    // Step 1: Check if token exists
    if (!token) {
      return {
        isValid: false,
        hasContractorId: false,
        hasContractorRole: false,
        error: "No token provided",
        shouldShowSecurityModal: true,
      };
    }

    // Step 2: Decode and validate JWT format
    const decoded = decodeToken(token);
    if (!decoded) {
      return {
        isValid: false,
        hasContractorId: false,
        hasContractorRole: false,
        error: "Invalid token format",
        shouldShowSecurityModal: true,
      };
    }

    // Step 3: Check token expiration
    if (isTokenExpired(token)) {
      return {
        isValid: false,
        hasContractorId: false,
        hasContractorRole: false,
        error: "Token expired",
        shouldShowSecurityModal: true,
      };
    }

    // Step 4: Check for contractor_id presence OR contractor-related role
    const hasContractor = hasContractorId(token);
    
    // Check if user has a contractor-related role (contractor, contractor_project_manager, etc.)
    // These roles should be allowed even if contractor_id is not in the token
    const contractorRelatedRoles = ["contractor", "contractor_project_manager"];
    const userRole = decoded.role;
    const hasContractorRole = contractorRelatedRoles.includes(userRole);

    // If user has contractor_id OR has a contractor-related role, they're valid
    if (!hasContractor && !hasContractorRole) {
      return {
        isValid: true,
        hasContractorId: false,
        hasContractorRole: false,
        error: "Token missing contractor_id",
        shouldShowSecurityModal: true,
      };
    }

    // Step 5: Final validation result
    // Return success if either contractor_id exists OR user has contractor-related role
    return {
      isValid: true,
      hasContractorId: hasContractor,
      hasContractorRole: hasContractorRole,
      error: null,
      shouldShowSecurityModal: false,
    };
  } catch (error) {
    console.error("❌ [SECURITY_VALIDATION] CRITICAL ERROR:", error);

    return {
      isValid: false,
      hasContractorId: false,
      hasContractorRole: false,
      error: error.message,
      shouldShowSecurityModal: true,
    };
  }
};
